import type { Currency } from "firebase/analytics";
import Decimal from "decimal.js";
import { isNil } from "lodash";

export type PriceValue = number | "UNAVAILABLE" | "ON_DEMAND" | "INVALID";

type InternalValue = Decimal | "UNAVAILABLE" | "ON_DEMAND" | "INVALID";

export class Price {
  private static readonly INVALID_INSTANCE = new Price("INVALID");
  private static readonly ON_DEMAND_INSTANCE = new Price("ON_DEMAND");
  private static readonly UNAVAILABLE_INSTANCE = new Price("UNAVAILABLE");

  private readonly _value: InternalValue;

  constructor(value: PriceValue) {
    if (value !== "UNAVAILABLE" && value !== "ON_DEMAND" && value !== "INVALID" && !(typeof value === "number")) {
      throw new Error(`Invalid value provided for option price: ${value}`);
    }
    this._value = typeof value === "number" ? new Decimal(value) : value;
  }

  get value(): PriceValue {
    if (this._value instanceof Decimal) {
      return this._value.toNumber();
    }
    return this._value;
  }

  multiply(quantity: number): Price {
    if (this._value instanceof Decimal) {
      return new Price(this._value.mul(quantity).toNumber());
    }
    return new Price(this.value);
  }

  add(value: number | Price): Price {
    if (typeof value === "number") {
      if (this._value instanceof Decimal) {
        return new Price(this._value.add(value).toNumber());
      }
      return new Price(this.value);
    }
    return this.addOther(value);
  }

  subtract(value: number | Price): Price {
    if (typeof value === "number") {
      if (this._value instanceof Decimal) {
        return new Price(this._value.sub(value).toNumber());
      }
      return new Price(this.value);
    }
    return this.subtractOther(value);
  }

  divideBy(value: number): Price {
    if (this._value instanceof Decimal) {
      return this.value !== 0 ? new Price(this._value.div(value).toNumber()) : new Price(0);
    }
    return new Price(this.value);
  }

  isGreaterThanOrEqual(value: number): boolean {
    if (typeof this.value === "number") {
      return this.value >= value;
    }
    return false;
  }

  isGreaterThan(value: number): boolean {
    if (typeof this.value === "number") {
      return this.value > value;
    }
    return false;
  }

  withMargin(margin: number): Price {
    return this.multiply(1 + margin / 100);
  }

  round(precision?: number): Price {
    if (this._value instanceof Decimal) {
      const places = precision ?? 0;
      if (places < 0) {
        const factor = new Decimal(10).pow(-places);
        return new Price(this._value.div(factor).toDecimalPlaces(0).mul(factor).toNumber());
      }
      return new Price(this._value.toDecimalPlaces(places).toNumber());
    }
    return new Price(this.value);
  }

  isPresent(): boolean {
    return typeof this.value === "number";
  }

  toDecimal(): Decimal | null {
    if (this._value instanceof Decimal) {
      return this._value;
    }
    return null;
  }

  equals(other: Price | null | undefined): boolean {
    if (other === null || other === undefined) {
      return false;
    }
    return this.value === other.value;
  }

  toDisplayValue(currency: Currency | null | undefined): string {
    if (typeof this.value === "number") {
      return currency ? `${this.value}\xa0${currency}` : `${this.value.toFixed(2)}`;
    }
    if (this.value === "ON_DEMAND") {
      return "na\xa0zapytanie";
    }
    if (this.value === "UNAVAILABLE") {
      return "niedostępny";
    }
    return "błąd";
  }

  protected addOther(other: Price): Price {
    if (this._value instanceof Decimal && other._value instanceof Decimal) {
      return new Price(this._value.add(other._value).toNumber());
    }
    if (this.value === "INVALID" || other.value === "INVALID") {
      return Price.invalid;
    }
    if (this.value === "UNAVAILABLE" || other.value === "UNAVAILABLE") {
      return Price.unavailable;
    }
    return Price.onDemand;
  }

  private subtractOther(other: Price): Price {
    if (this._value instanceof Decimal && other._value instanceof Decimal) {
      return new Price(this._value.sub(other._value).toNumber());
    }
    if (this.value === "INVALID" || other.value === "INVALID") {
      return Price.invalid;
    }
    if (this.value === "UNAVAILABLE" || other.value === "UNAVAILABLE") {
      return Price.unavailable;
    }
    return Price.onDemand;
  }

  static fromNullable(value: PriceValue | null | undefined): Price {
    return new Price(isNil(value) ? "ON_DEMAND" : value);
  }

  static get unavailable(): Price {
    return Price.UNAVAILABLE_INSTANCE;
  }

  static get onDemand(): Price {
    return Price.ON_DEMAND_INSTANCE;
  }

  static get invalid(): Price {
    return Price.INVALID_INSTANCE;
  }
}
